import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { deptStatusOf, listDepts, type DeptRow } from '../../api/orgApi.js';

export function DepartmentListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DeptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listDepts(token)
      .then(r => setRows(r.data))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <main aria-label="department-list" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>{t('org.department.list.title')}</h1>
        <Link to="/org/departments/new" style={{ padding: '8px 16px', borderRadius: 6, background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', textDecoration: 'none' }}>{t('org.department.list.new')}</Link>
      </div>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p>{t('org.department.list.empty')}</p>}
      {rows.length > 0 && (
        <DataGrid<DeptRow>
          caption="departments"
          rows={rows}
          getRowId={(d) => d.deptId}
          rowTestId={(d) => `dept-${d.deptId}`}
          columns={[
            {
              key: 'code',
              header: t('org.col.code'),
              value: (d) => d.deptCode,
              render: (d) => (
                <Link to={`/org/departments/${d.deptId}/edit`} style={{ color: 'var(--color-text-link)' }}>{d.deptCode}</Link>
              ),
            },
            { key: 'name', header: t('org.col.name'), value: (d) => d.deptName },
            { key: 'headCount', header: t('org.col.headCount'), value: (d) => d.headCount ?? '—' },
            {
              key: 'status',
              header: t('org.col.status'),
              value: (d) => deptStatusOf(d),
              render: (d) => (
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: deptStatusOf(d) === 'ACTIVE' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)' }}>{deptStatusOf(d)}</span>
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
